import * as React from 'react'
import { cn } from '@/lib/utils'

// ─── SVG Icon Library ─────────────────────────────────────────────────────────
// Each icon is a clean 26×26 hand-crafted SVG with filled opacity layer + outline stroke.
// Usage: <CardIcon name="crm" color="blue" />

type IconName =
  | 'crm'
  | 'health'
  | 'analytics'
  | 'commerce'
  | 'learning'
  | 'sustainability'
  | 'finance'
  | 'security'
  | 'automation'
  | 'messaging'
  | 'cloud'
  | 'ai'

const icons: Record<IconName, React.FC<{ className?: string }>> = {
  crm: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <rect x="3" y="5" width="20" height="4" rx="2" fill="currentColor" opacity=".15"/>
      <rect x="3" y="5" width="20" height="4" rx="2" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="3" y="11" width="20" height="4" rx="2" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="3" y="17" width="20" height="4" rx="2" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="7.5" cy="7" r="1" fill="currentColor"/>
      <circle cx="7.5" cy="13" r="1" fill="currentColor"/>
      <circle cx="7.5" cy="19" r="1" fill="currentColor"/>
    </svg>
  ),
  health: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <path d="M13 3C13 3 7 6 4 10.5C4 15.5 7.5 20 13 22C18.5 20 22 15.5 22 10.5C19 6 13 3 13 3Z"
        fill="currentColor" opacity=".12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M9.5 13l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  analytics: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <rect x="3" y="14" width="5" height="8" rx="1.5" fill="currentColor" opacity=".15" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="10.5" y="9" width="5" height="13" rx="1.5" fill="currentColor" opacity=".15" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="18" y="4" width="5" height="18" rx="1.5" fill="currentColor" opacity=".15" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M4 11l5-4 5.5 3.5L21 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="21" cy="5" r="1.5" fill="currentColor"/>
    </svg>
  ),
  commerce: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <rect x="3" y="8" width="20" height="14" rx="2.5" fill="currentColor" opacity=".12" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 8V6a5 5 0 0 1 10 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="13" cy="15" r="2" fill="currentColor" opacity=".25"/>
      <circle cx="13" cy="15" r="2" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M13 16.5v1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  learning: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <path d="M3 10l10-6 10 6-10 6L3 10z" fill="currentColor" opacity=".12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M7 12.5v5c0 0 2.5 2.5 6 2.5s6-2.5 6-2.5v-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M20 10v7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  sustainability: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <circle cx="13" cy="13" r="9" fill="currentColor" opacity=".1" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M13 4v18M4 13h18" stroke="currentColor" strokeWidth="1.1" strokeDasharray="2 2" opacity=".45"/>
      <path d="M13 4C10 7 8.5 10 8.5 13s1.5 6 4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M13 4C16 7 17.5 10 17.5 13s-1.5 6-4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  finance: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <rect x="3" y="6" width="20" height="14" rx="3" fill="currentColor" opacity=".12" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M3 11h20" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="8" cy="16" r="1.5" fill="currentColor" opacity=".5"/>
      <path d="M13 14h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  security: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <path d="M13 3C13 3 5 6.5 5 10v6c0 3.5 4 6.5 8 8 4-1.5 8-4.5 8-8v-6C21 6.5 13 3 13 3Z"
        fill="currentColor" opacity=".12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M9.5 13.5l2 2 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  automation: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <circle cx="13" cy="13" r="3" fill="currentColor" opacity=".2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M13 4v2.5M13 19.5V22M4 13h2.5M19.5 13H22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M6.5 6.5l1.8 1.8M17.7 17.7l1.8 1.8M6.5 19.5l1.8-1.8M17.7 8.3l1.8-1.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  messaging: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <path d="M4 5h18a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1Z"
        fill="currentColor" opacity=".12" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M8 11h10M8 14.5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  cloud: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <path d="M7 19a5 5 0 1 1 1.5-9.8A6 6 0 0 1 20 14a4 4 0 0 1-1 7.9" fill="currentColor" opacity=".12"/>
      <path d="M7 19a5 5 0 1 1 1.5-9.8A6 6 0 0 1 20 14a4 4 0 0 1-1 7.9" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
      <path d="M13 15v-5M11 12l2-2 2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  ai: (p) => (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none" {...p}>
      <rect x="5" y="5" width="16" height="16" rx="4" fill="currentColor" opacity=".12" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="9.5" cy="9.5" r="1.5" fill="currentColor" opacity=".6"/>
      <circle cx="16.5" cy="9.5" r="1.5" fill="currentColor" opacity=".6"/>
      <path d="M9 15.5c1 1.5 7 1.5 8 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M13 3v2M13 21v2M3 13h2M21 13h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity=".5"/>
    </svg>
  ),
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type CardColor = 'blue' | 'green' | 'amber' | 'coral' | 'purple' | 'teal'

export interface CardStat { value: string; label: string }

export interface PremiumCardProps {
  /** One of the built-in icon names, or pass a custom ReactNode via `customIcon` */
  icon?: IconName
  /** Override with any ReactNode (e.g. your own SVG or img) */
  customIcon?: React.ReactNode
  /** Optional badge label. Omit to hide. */
  badge?: string
  title: string
  description: string
  /** Up to 3 stat items */
  stats?: CardStat[]
  /** Tag chips (used if no stats) */
  tags?: string[]
  /** Left-side footer meta text */
  meta?: string
  /** CTA label. Defaults to "Explore". */
  ctaLabel?: string
  /** Accessible name for the CTA link. Falls back to `ctaLabel`. */
  ctaAriaLabel?: string
  href?: string
  onClick?: () => void
  color?: CardColor
  className?: string
}

// ─── Color tokens ─────────────────────────────────────────────────────────────

const brandToken: {
  accent: string
  accentSoft: string
  accentRing: string
  badge: string
  cta: string
  ctaShadow: string
  glow: string
} = {
  accent: 'from-[#2073C1] to-[#5BA7EA]',
  accentSoft: 'bg-[#2073C1] text-white',
  accentRing: 'ring-[#2073C1]/20',
  badge: 'bg-[#2073C1] text-white ring-[#2073C1]/15',
  cta: 'text-[#2073C1]',
  ctaShadow: '',
  glow: 'from-[#2073C1]/10 via-[#5BA7EA]/5 to-transparent',
}

const tokens: Record<CardColor, typeof brandToken> = {
  blue: brandToken,
  green: brandToken,
  amber: brandToken,
  coral: brandToken,
  purple: brandToken,
  teal: brandToken,
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PremiumCard({
  icon,
  customIcon,
  badge,
  title,
  description,
  stats,
  tags,
  meta,
  ctaLabel = 'Explore',
  ctaAriaLabel,
  href = '#',
  onClick,
  color = 'blue',
  className,
}: PremiumCardProps) {
  const t = tokens[color]
  const IconSVG = icon ? icons[icon] : null
  const iconNode = customIcon ?? (IconSVG ? <IconSVG /> : null)
  const initials = title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('')

  return (
    <div
      className={cn(
        'group relative isolate flex min-h-[270px] flex-col overflow-hidden rounded-none border border-[#2073C1]/18 bg-white p-5',
        'shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_42px_-28px_rgba(32,115,193,0.45)]',
        'transition-[border-color,box-shadow,transform] duration-300 ease-out hover:-translate-y-1 hover:border-[#2073C1]/35 hover:shadow-[0_24px_64px_-30px_rgba(32,115,193,0.5),0_10px_24px_-18px_rgba(15,23,42,0.18)]',
        className,
      )}
    >
      <div className={cn('pointer-events-none absolute -right-16 -top-20 h-44 w-44 rounded-full bg-gradient-to-br blur-2xl transition-opacity duration-300 group-hover:opacity-100', t.glow)} />
      <div className="pointer-events-none absolute inset-0 rounded-none ring-1 ring-inset ring-white/80" />

      <div className="relative z-10 flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              'relative flex h-16 w-16 shrink-0 items-center justify-start text-[#2073C1]',
              'transition-transform duration-300 group-hover:scale-[1.04]',
            )}
          >
            {iconNode ? (
              <div className="[&_svg]:h-12 [&_svg]:w-12">
                {iconNode}
              </div>
            ) : (
              <span className="text-xl font-black tracking-normal">{initials || 'PC'}</span>
            )}
          </div>
        </div>

        {badge && (
          <span className={cn('max-w-[45%] truncate rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] ring-1', t.badge)}>
            {badge}
          </span>
        )}
      </div>

      <div className="relative z-10 mt-5">
        <h3 className="line-clamp-2 text-[18px] font-bold leading-snug tracking-normal text-slate-950">
          {title}
        </h3>
        <p className="mt-2 line-clamp-3 text-[13px] font-medium leading-relaxed text-slate-600">
          {description}
        </p>
      </div>

      {stats && stats.length > 0 && (
        <div className="relative z-10 mt-5 grid grid-cols-3 gap-2">
          {stats.slice(0, 3).map((s, i) => (
            <div key={i} className="rounded-xl border border-[#2073C1]/12 bg-[#F4F9FE] px-3 py-2">
              <span className="block text-[15px] font-black leading-tight text-[#2073C1]">{s.value}</span>
              <span className="mt-0.5 block truncate text-[10px] font-semibold leading-tight text-slate-500">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {!stats && tags && tags.length > 0 && (
        <div className="relative z-10 mt-5 flex flex-wrap gap-2">
          {tags.slice(0, 4).map((tag, i) => (
            <span key={i} className="rounded-lg border border-[#2073C1]/12 bg-[#F4F9FE] px-2.5 py-1 text-[11px] font-semibold text-[#1E5F9D]">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="relative z-10 mt-auto pt-5">
        <div className="mb-4 h-px bg-gradient-to-r from-transparent via-[#2073C1]/16 to-transparent" />

        <div className="flex items-center justify-between gap-3">
          {meta && (
            <span className="min-w-0 truncate text-xs font-semibold text-slate-500">{meta}</span>
          )}
          <a
            href={href}
            aria-label={ctaAriaLabel ?? ctaLabel}
            onClick={(e) => { if (onClick) { e.preventDefault(); onClick() } }}
            className={cn(
              'ml-auto inline-flex shrink-0 items-center justify-center gap-2 text-[16px] font-bold no-underline',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2073C1]/30 focus-visible:ring-offset-2',
              t.cta,
              t.ctaShadow,
              t.accentRing,
            )}
          >
            {ctaLabel}
            <svg width="15" height="15" viewBox="0 0 14 14" fill="none" aria-hidden="true"
              className="shrink-0">
              <path d="M3 7h8M7.5 4l3 3-3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </div>
  )
}

export default PremiumCard

